// src/components/registration/steps/StepSystemPermissions.jsx
import React, { useState, useEffect } from "react";
import StepBar from "../../../utils/StepBar";
import MultiStyleButton from "../../systemUtils/MultiStyleButton";
import heroLogo from "../../../../images/heroLogo.png";
import UserSystemCard from "./supportComponents/UserSystemCard";
import SupplierSystemCard from "./supportComponents/SupplierSystemCard";
import ClaimsSystemCard from "./supportComponents/ClaimsSystemCard";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import "../RegisterSteps.css";
import LoadingCircle from "../../../utils/LoadingCircle";
import { useDispatch, useSelector } from "react-redux";
import { setRegistrationStep } from "../../../../redux/actionsFolder/registrationActions";
import { useNavigate } from "react-router-dom";

const StepSystemPermissions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registrationData = useSelector(
    (state) => state.registration.registrationData
  );

  // Ensure previous steps are complete
  useEffect(() => {
    if (
      !registrationData.userEmail ||
      !registrationData.company ||
      !registrationData.collectionBrand
    ) {
      navigate("/register-hero/welcome");
    }
  }, [registrationData, navigate]);

  const [companyStructureData, setCompanyStructureData] = useState(null);
  const [systemPermissions, setSystemPermissions] = useState({
    usersSystem: {
      defaultOrCustom: "default",
      allowRoleLevelConfig: false,
      addUserConfig: {},
    },
    suppliersSystem: {
      defaultOrCustom: "default",
      approvalGate: {
        isRequired: false,
        needRoleLevel: false,
        approverGroups: [],
        approverRoles: [],
      },
    },
    claimsSystem: {
      defaultOrCustom: "default",
      approvalGate: {
        isRequired: false,
        needRoleLevel: false,
        useValueCondition: false,
        approverGroups: [],
        approverRolesBelow: [],
        approverRolesAbove: [],
        thresholdValue: "",
      },
    },
  });

  useEffect(() => {
    const app = getApp();
    const functions = getFunctions(app);
    const firestoreOps = httpsCallable(functions, "firestoreOperations");

    const fetchCompanyStructure = async () => {
      try {
        const response = await firestoreOps({
          operation: "read",
          company: registrationData.company,
          collectionBrand: registrationData.collectionBrand,
          systemName: "companyConfig",
          uniqueId: "companyStructure",
        });

        if (response.data && response.data.data) {
          setCompanyStructureData(response.data.data);
        } else {
          console.warn("No companyStructure doc found. Using fallback data.");
        }
      } catch (error) {
        console.error("Error fetching companyStructure:", error);
      }
    };

    fetchCompanyStructure();
  }, [registrationData.company, registrationData.collectionBrand]);

  const handleUpdateSystemPermissions = (systemKey, updatedObject) => {
    setSystemPermissions((prev) => ({
      ...prev,
      [systemKey]: {
        ...prev[systemKey],
        ...updatedObject,
      },
    }));
  };

  const handleFinish = async () => {
    try {
      const app = getApp();
      const functions = getFunctions(app);
      const firestoreOps = httpsCallable(functions, "firestoreOperations");

      // Write systemPermissions
      await firestoreOps({
        operation: "write",
        company: registrationData.company,
        collectionBrand: registrationData.collectionBrand,
        systemName: "companyConfig",
        uniqueId: "systemPermissions",
        documentData: systemPermissions,
      });

      // Write configStatus
      await firestoreOps({
        operation: "write",
        company: registrationData.company,
        collectionBrand: registrationData.collectionBrand,
        systemName: "companyConfig",
        uniqueId: "configStatus",
        documentData: {
          isComplete: true,
        },
      });

      console.log("System Permissions & configStatus saved successfully!");

      // Send congratulations email to the learner
      try {
        const sendEmailFn = httpsCallable(functions, "sendEmail");

        const htmlData = `<div style="
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  max-width: 600px;
  margin: 40px auto;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  background: #ffffff;
">

  <!-- Header -->
  <div style="
    background-color: #1e2a38;
    color: #ffffff;
    padding: 24px 28px;
    border-bottom: 4px solid #d4af37;
    text-align: center;
  ">
    <h1 style="margin: 0; font-size: 2rem;">🎉 Congratulations!</h1>
    <h2 style="margin: 10px 0 0 0; font-size: 1.5rem; color: #d4af37;">You're Just 1 Step Away!</h2>
  </div>

  <!-- Body -->
  <div style="
    padding: 28px;
    color: #333333;
    font-size: 1rem;
    line-height: 1.7;
  ">
    <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 20px;">
      <p style="font-size: 16px; line-height: 1.6; color: #2c3e50; margin-bottom: 15px;">
        Great news! You've successfully completed your registration setup. Your account has been configured and you're now ready for the final step.
      </p>
      
      <p style="font-size: 16px; line-height: 1.6; color: #2c3e50; margin-bottom: 20px;">
        <strong>Next Step:</strong> Complete your baseline assessment to finalize your learner profile and begin your learning journey.
      </p>
      
      <div style="background-color: #e8f4fd; padding: 15px; border-left: 4px solid #3498db; border-radius: 5px;">
        <p style="font-size: 14px; color: #2c3e50; margin: 0;">
          <strong>What to expect:</strong> The baseline assessment will help us understand your current knowledge level and tailor your learning experience accordingly.
        </p>
      </div>
    </div>

    <p style="margin-top: 40px;">
      Thank you for choosing our learning platform. We're excited to support your educational journey!<br/>
      <br/>
      Best regards,<br/>
      Your <strong>Hero</strong> Team
    </p>

  </div>

  <!-- Footer -->
  <div style="
    background-color: #f7f7f7;
    color: #777777;
    padding: 18px 28px;
    font-size: 0.85rem;
    text-align: center;
    border-top: 1px solid #dddddd;
  ">
    This is an automated message from <strong>Hero Learning Platform</strong>.
  </div>

</div>`;

        const emailData = {
          to: registrationData.userEmail,
          from: "Hero Admin <noreply@mail.andus.co.za>",
          subject:
            "Congratulations! You're Almost There - Complete Your Baseline Assessment",
          html: htmlData,
          isHTML: true,
        };

        await sendEmailFn(emailData);

        console.log(
          "Congratulations email sent successfully to:",
          registrationData.userEmail
        );
      } catch (emailError) {
        console.error("Error sending congratulations email:", emailError);
        // Don't block the registration completion if email fails
      }

      dispatch(setRegistrationStep("completed"));
      navigate("/hero-dashboard");
    } catch (error) {
      console.error("Error saving configuration:", error);
      alert("An error occurred while saving. Please try again.");
    }
  };

  return (
    <div className="step-system-permissions-container">
      <div className="top-bar">
        <div className="logo-container">
          <img src={heroLogo} alt="Hero Logo" className="logo-small" />
        </div>
        <StepBar totalSteps={4} currentStep={4} />
      </div>

      <h2 className="step-title centered">Configure System Permissions</h2>
      <p className="step-description centered">
        In this step, you'll define who can perform actions in each system
        (Users, Suppliers, Claims), and whether any approval gates are required.
        By default, each hierarchy can manage roles at the same or lower level,
        but you can enable custom rules below.
      </p>

      <div className="card-container-systemPermissions">
        <UserSystemCard
          companyStructure={companyStructureData}
          userSystem={systemPermissions.usersSystem}
          onChange={(updatedObj) =>
            handleUpdateSystemPermissions("usersSystem", updatedObj)
          }
        />
        <SupplierSystemCard
          companyStructure={companyStructureData}
          suppliersSystem={systemPermissions.suppliersSystem}
          onChange={(updatedObj) =>
            handleUpdateSystemPermissions("suppliersSystem", updatedObj)
          }
        />
        <ClaimsSystemCard
          companyStructure={companyStructureData}
          claimsSystem={systemPermissions.claimsSystem}
          onChange={(updatedObj) =>
            handleUpdateSystemPermissions("claimsSystem", updatedObj)
          }
        />
      </div>

      <div className="continue-button-container">
        <MultiStyleButton
          label="Finish"
          buttonType="approve"
          onClick={handleFinish}
        />
      </div>
    </div>
  );
};

export default StepSystemPermissions;
